import React, { useEffect, useState, useMemo } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';

export default function PayPalCheckout({ items, onSuccess, amount }) {
  const { api } = useApp();
  const [clientId, setClientId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/config/public').then((r) => {
      setClientId(r.data.paypal_client_id || '');
      setEnabled(!!r.data.paypal_enabled);
    }).finally(() => setLoading(false));
  }, [api]);

  const ppOptions = useMemo(() => ({ 'client-id': clientId, currency: 'USD', intent: 'capture' }), [clientId]);

  if (loading) return <div className="mt-8 text-center font-light text-sm text-muted-foreground" data-testid="pp-loading">Loading PayPal…</div>;

  if (!enabled) {
    return (
      <div className="mt-8 p-5 border border-border bg-secondary" data-testid="pp-disabled">
        <div className="overline mb-2">PayPal Not Yet Configured</div>
        <p className="text-sm font-light text-muted-foreground">PayPal is ready to enable — paste your PayPal Client ID and Secret into the backend <code>.env</code> file (PAYPAL_CLIENT_ID, PAYPAL_CLIENT_SECRET) and the Smart Buttons will activate instantly. No code changes needed.</p>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="pp-buttons-wrap">
      <PayPalScriptProvider options={ppOptions}>
        <PayPalButtons
          style={{ layout: 'vertical', color: 'black', shape: 'rect', label: 'paypal' }}
          createOrder={async () => {
            try {
              const { data } = await api.post('/checkout/paypal/create-order', items.map((i) => ({ product_id: i.id, quantity: i.quantity })));
              return data.id;
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Could not start PayPal');
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              await api.post(`/checkout/paypal/capture/${data.orderID}`);
              toast.success('Payment successful');
              onSuccess?.(data.orderID);
            } catch (e) {
              toast.error('Capture failed');
            }
          }}
          onError={() => toast.error('PayPal error')}
        />
      </PayPalScriptProvider>
    </div>
  );
}
